import React, { useState, useEffect } from 'react';
import { Cookie, Settings, X, Check } from 'lucide-react';
import './CookieConsent.css';

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
}

const STORAGE_KEY = 'cookie-preferences';

const CookieConsent: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true, // Always required
    analytics: false,
  });

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      setShowBanner(true);
      return;
    }

    try {
      const parsed = JSON.parse(saved) as Partial<CookiePreferences>;
      const normalized: CookiePreferences = {
        essential: true,
        analytics: Boolean(parsed.analytics),
      };
      setPreferences(normalized);
      applyCookiePreferences(normalized);
    } catch {
      // Corrupted value — ask again.
      setShowBanner(true);
    }
  }, []);

  const applyCookiePreferences = (prefs: CookiePreferences): void => {
    // Privacy-focused analytics (Umami) will be initialized here once added.
    // Umami is cookieless and collects no personal data; we still honor this
    // choice so visitors stay in control.
    if (prefs.analytics) {
      // TODO: enable Umami analytics
    } else {
      // TODO: ensure Umami analytics is disabled
    }
  };

  const persist = (prefs: CookiePreferences): void => {
    setPreferences(prefs);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    applyCookiePreferences(prefs);
  };

  const handleAcceptAll = (): void => {
    persist({ essential: true, analytics: true });
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleAcceptEssential = (): void => {
    persist({ essential: true, analytics: false });
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleSavePreferences = (): void => {
    persist(preferences);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleToggleAnalytics = (): void => {
    setPreferences((prev) => ({ ...prev, analytics: !prev.analytics }));
  };

  if (!showBanner) {
    return null;
  }

  return (
    <>
      {/* Main Cookie Banner */}
      {!showSettings && (
        <div className="cookie-banner">
          <div className="cookie-content">
            <div className="cookie-icon">
              <Cookie size={24} />
            </div>

            <div className="cookie-text">
              <h3>Cookies</h3>
              <p>
                Essential storage keeps the site working. Optional analytics are
                privacy-friendly — no personal data, no ads.{' '}
                <button
                  type="button"
                  className="cookie-text-link"
                  onClick={() => setShowSettings(true)}
                >
                  Learn more
                </button>
              </p>
            </div>

            <div className="cookie-actions">
              <button
                type="button"
                className="cookie-btn cookie-btn-secondary"
                onClick={() => setShowSettings(true)}
              >
                <Settings size={12} />
                Customize
              </button>
              <button
                type="button"
                className="cookie-btn cookie-btn-secondary"
                onClick={handleAcceptEssential}
              >
                Essential only
              </button>
              <button
                type="button"
                className="cookie-btn cookie-btn-primary"
                onClick={handleAcceptAll}
              >
                Accept all
              </button>
            </div>

            <button
              className="cookie-close"
              onClick={handleAcceptEssential}
              aria-label="Close cookie banner"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Cookie Settings Modal */}
      {showSettings && (
        <div className="cookie-settings">
          <div className="cookie-settings-content">
            <div className="settings-header">
              <div className="settings-icon">
                <Settings size={24} />
              </div>
              <h2>Cookie Preferences</h2>
              <p>Manage your cookie preferences below</p>
            </div>

            <div className="settings-options">
              <div className="cookie-option essential">
                <div className="option-header">
                  <div className="option-info">
                    <h4>Essential Cookies</h4>
                    <p>Required for the website to function properly</p>
                  </div>
                  <div className="option-toggle">
                    <input
                      type="checkbox"
                      checked={preferences.essential}
                      disabled
                      readOnly
                    />
                    <span className="toggle-label">Always Active</span>
                  </div>
                </div>
                <div className="option-details">
                  <p>
                    These remember your cookie choice and keep the site working.
                    They never track you and can&apos;t be turned off.
                  </p>
                </div>
              </div>

              <div className="cookie-option">
                <div className="option-header">
                  <div className="option-info">
                    <h4>Analytics</h4>
                    <p>Privacy-friendly insights into which pages people visit</p>
                  </div>
                  <div className="option-toggle">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={handleToggleAnalytics}
                    />
                    <span className="toggle-label">
                      {preferences.analytics ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>
                <div className="option-details">
                  <p>
                    We plan to use Umami, a privacy-focused analytics tool that
                    is cookieless, collects no personal data, and never tracks
                    you across other sites.
                  </p>
                </div>
              </div>
            </div>

            <div className="settings-actions">
              <button
                type="button"
                className="cookie-btn cookie-btn-secondary"
                onClick={() => setShowSettings(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="cookie-btn cookie-btn-primary"
                onClick={handleSavePreferences}
              >
                <Check size={14} />
                Save
              </button>
            </div>

            <div className="settings-footer">
              <p>
                For more information about how we use cookies, please see our{' '}
                <a href="/privacy">Privacy Policy</a>.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
